import {resolveUserAuthorization} from '../auth/authorization';
import {resolveClientCredentialsAuthorization} from '../auth/clientCredentials';
import {isBrowserRuntime} from '../runtime/browser';
import {ClientCredentialsTarget} from '../types/auth';
import {
  RequestAuthMode,
  RequestAuthTokenSource,
  RequestExecutionMode,
  type ResolvedRequest,
  type ResolveRequestInput,
  RouteFamily,
  type RouteRule,
  type TargetedRouteRule,
} from '../types/route';
import {normalizeHeadersInit, toHeadersRecord} from '../utils/headers';
import {
  combinePathSegments,
  generateValidUrl,
  resolveInternalOrigin,
} from '../utils/url';

import {
  buildRouteRules,
  findMatchingRouteRule,
  resolveBackendTestEnvironment,
  resolvePlutusGatewayHost,
} from './routeRules';

const hasTarget = (rule: RouteRule | undefined): rule is TargetedRouteRule => {
  return Boolean(rule?.target);
};

const applyBearerToken = (headers: Headers, token: string): void => {
  headers.set('Authorization', `Bearer ${token}`);
  headers.delete('cookie');
};

const rewriteRequestUrl = ({
  parsedUrl,
  routeRule,
}: {
  parsedUrl: URL;
  routeRule: TargetedRouteRule;
}): {logicalPath: string; url: string} => {
  const targetUrl = generateValidUrl(routeRule.target);
  const rewrittenPath = parsedUrl.pathname.replace(
    routeRule.prefix,
    routeRule.rewrite,
  );

  targetUrl.pathname = combinePathSegments(targetUrl.pathname, rewrittenPath);
  targetUrl.search = parsedUrl.search;

  return {
    logicalPath: `${routeRule.family}:${rewrittenPath}`,
    url: targetUrl.toString(),
  };
};

const createPassthroughResolution = ({
  url,
  headers,
  executionMode = RequestExecutionMode.PASSTHROUGH,
}: {
  url: string;
  headers?: Record<string, string>;
  executionMode?: ResolvedRequest['executionMode'];
}): ResolvedRequest => {
  let parsedUrl = generateValidUrl(url);

  if (executionMode === RequestExecutionMode.BROKERED) {
    const internalOrigin = resolveInternalOrigin(parsedUrl.origin);
    const hasDifferentOrigin = internalOrigin !== parsedUrl.origin;

    parsedUrl = hasDifferentOrigin
      ? new URL(
          `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`,
          internalOrigin,
        )
      : parsedUrl;
  }

  return {
    url: parsedUrl.toString(),
    headers,
    family: RouteFamily.LOCAL,
    executionMode,
    authMode: RequestAuthMode.NONE,
    tokenSource: RequestAuthTokenSource.NONE,
    logicalPath: parsedUrl.pathname,
  };
};

const createDirectResolution = ({
  parsedUrl,
  routeRule,
  headers,
  authMode,
  tokenSource,
}: {
  parsedUrl: URL;
  routeRule: TargetedRouteRule;
  headers: Headers;
  authMode: RequestAuthMode;
  tokenSource: RequestAuthTokenSource;
}): ResolvedRequest => {
  const rewrittenRequest = rewriteRequestUrl({
    parsedUrl,
    routeRule,
  });

  return {
    url: rewrittenRequest.url,
    headers: toHeadersRecord(headers),
    family: routeRule.family,
    executionMode: RequestExecutionMode.DIRECT,
    authMode,
    tokenSource,
    matchedPrefix: routeRule.prefix,
    logicalPath: rewrittenRequest.logicalPath,
    environment: resolveBackendTestEnvironment(),
  };
};

const createDirectUnauthenticatedResolution = ({
  parsedUrl,
  routeRule,
  headers,
}: {
  parsedUrl: URL;
  routeRule: TargetedRouteRule;
  headers: Headers;
}): ResolvedRequest => {
  return createDirectResolution({
    parsedUrl,
    routeRule,
    headers,
    authMode: RequestAuthMode.NONE,
    tokenSource: RequestAuthTokenSource.NONE,
  });
};

const resolveClientCredentialsTarget = (
  family: RouteFamily,
): ClientCredentialsTarget | undefined => {
  if (family === RouteFamily.CORE_CC) {
    return ClientCredentialsTarget.CORE_CC;
  }

  if (family === RouteFamily.PLUTUS_CC) {
    return ClientCredentialsTarget.PLUTUS_CC;
  }

  if (family === RouteFamily.APOLLO_CC) {
    return ClientCredentialsTarget.APOLLO_CC;
  }

  return undefined;
};

const resolveUserAuthenticatedRequest = async ({
  parsedUrl,
  routeRule,
  headers,
  originalUrl,
}: {
  parsedUrl: URL;
  routeRule: TargetedRouteRule;
  headers: Headers;
  originalUrl: string;
}): Promise<ResolvedRequest> => {
  const userAuthorization = await resolveUserAuthorization({
    requestOrigin: parsedUrl.origin,
    headers,
  });

  if (!userAuthorization.token) {
    return createPassthroughResolution({
      url: originalUrl,
      headers: toHeadersRecord(headers),
      executionMode: RequestExecutionMode.BROKERED,
    });
  }

  applyBearerToken(headers, userAuthorization.token);

  return createDirectResolution({
    parsedUrl,
    routeRule,
    headers,
    authMode: RequestAuthMode.USER_BEARER,
    tokenSource: userAuthorization.tokenSource,
  });
};

const resolveClientCredentialsRequest = async ({
  parsedUrl,
  routeRule,
  headers,
  originalUrl,
}: {
  parsedUrl: URL;
  routeRule: TargetedRouteRule;
  headers: Headers;
  originalUrl: string;
}): Promise<ResolvedRequest> => {
  const target = resolveClientCredentialsTarget(routeRule.family);

  if (!target) {
    return createPassthroughResolution({
      url: originalUrl,
      headers: toHeadersRecord(headers),
    });
  }

  const clientCredentialsAuthorization =
    await resolveClientCredentialsAuthorization(target);

  if (!clientCredentialsAuthorization.token && isBrowserRuntime()) {
    return createPassthroughResolution({
      url: originalUrl,
      headers: toHeadersRecord(headers),
      executionMode: RequestExecutionMode.BROKERED,
    });
  }

  // On the server, fail fast instead of brokering back into the same local
  // proxy route and creating a recursive request loop.
  if (!clientCredentialsAuthorization.token) {
    return createDirectResolution({
      parsedUrl,
      routeRule,
      headers,
      authMode: RequestAuthMode.CLIENT_CREDENTIALS,
      tokenSource: RequestAuthTokenSource.NONE,
    });
  }

  applyBearerToken(headers, clientCredentialsAuthorization.token);

  return createDirectResolution({
    parsedUrl,
    routeRule,
    headers,
    authMode: RequestAuthMode.CLIENT_CREDENTIALS,
    tokenSource: clientCredentialsAuthorization.tokenSource,
  });
};

export const resolveRequest = async ({
  url,
  headers,
}: ResolveRequestInput): Promise<ResolvedRequest> => {
  const normalizedHeaders = normalizeHeadersInit(headers);
  const finalHeaders = new Headers(normalizedHeaders);
  const parsedUrl = generateValidUrl(url);
  const matchedRouteRule = findMatchingRouteRule(parsedUrl.pathname);

  if (!hasTarget(matchedRouteRule)) {
    return createPassthroughResolution({
      url,
      headers: normalizedHeaders,
    });
  }

  const existingAuthorizationHeader = finalHeaders.get('authorization');

  if (
    existingAuthorizationHeader &&
    !matchedRouteRule.requiresClientCredentials
  ) {
    finalHeaders.delete('cookie');

    return createDirectResolution({
      parsedUrl,
      routeRule: matchedRouteRule,
      headers: finalHeaders,
      authMode: matchedRouteRule.requiresUserAuth
        ? RequestAuthMode.USER_BEARER
        : RequestAuthMode.NONE,
      tokenSource: RequestAuthTokenSource.PREEXISTING_AUTHORIZATION_HEADER,
    });
  }

  if (matchedRouteRule.requiresUserAuth) {
    return resolveUserAuthenticatedRequest({
      parsedUrl,
      routeRule: matchedRouteRule,
      headers: finalHeaders,
      originalUrl: url,
    });
  }

  if (!matchedRouteRule.requiresClientCredentials) {
    return createDirectUnauthenticatedResolution({
      parsedUrl,
      routeRule: matchedRouteRule,
      headers: finalHeaders,
    });
  }

  return resolveClientCredentialsRequest({
    parsedUrl,
    routeRule: matchedRouteRule,
    headers: finalHeaders,
    originalUrl: url,
  });
};

export {
  buildRouteRules,
  resolveBackendTestEnvironment,
  resolvePlutusGatewayHost,
};
